"use client";
import React, {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { ArrowLeft, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { NAV_SECTIONS, SCROLL_GAP, SECTION_ROUTES } from "@/util/navigation";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { prefersReducedMotion } from "@/lib/prefers-reduced-motion";
import clsx from "clsx";
import { NavLinks } from "@/components/nav-links";

export function Header() {
  const router = useRouter();
  const route = usePathname();
  const onNav = (path: string, opts?: { replace?: boolean }) => {
    if (opts?.replace) router.replace(path);
    else router.push(path);
  };
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileOpenRef = useRef(mobileOpen);
  useEffect(() => {
    mobileOpenRef.current = mobileOpen;
  }, [mobileOpen]);
  const headerRef = useRef<HTMLElement | null>(null);
  const [headerH, setHeaderH] = useState(0);
  // Measured through ResizeObserver rather than reading offsetHeight in the
  // effect: the read forced a synchronous layout of the whole document right
  // after hydration, while the observer reports the same height once the
  // browser has laid the page out anyway, and keeps reporting on resize.
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const observer = new ResizeObserver(() => {
      setHeaderH(header.offsetHeight);
    });
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  const [menuAnimating, setMenuAnimating] = useState(false);
  const [menuEntering, setMenuEntering] = useState(false);
  const closeMenu = useCallback(() => {
    setMobileOpen(false);
    setMenuAnimating(true);
    setTimeout(() => setMenuAnimating(false), 250);
  }, []);
  // Ensure default anchor navigation accounts for sticky header
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.scrollPaddingTop;
    const prevBody = body.style.scrollPaddingTop;
    const value = `${headerH + SCROLL_GAP}px`;
    html.style.scrollPaddingTop = value;
    body.style.scrollPaddingTop = value;
    return () => {
      html.style.scrollPaddingTop = prevHtml;
      body.style.scrollPaddingTop = prevBody;
    };
  }, [headerH]);
  function toggleMenu() {
    if (!mobileOpen) {
      setMenuEntering(true);
      requestAnimationFrame(() => setMenuEntering(false));
      setMobileOpen(true);
    } else {
      closeMenu();
    }
  }
  useEffect(() => {
    if (!mobileOpenRef.current) return;
    startTransition(() => closeMenu());
  }, [route, closeMenu]);
  const isHomeRoute = route === "/";
  const [activeId, setActiveId] = useState<string>("");
  const isProgrammaticRef = useRef(false);
  const unlockTimerRef = useRef<number | undefined>(undefined);
  const pendingTargetRef = useRef<string | null>(null);
  const unlockRafRef = useRef<number | undefined>(undefined);
  const handledInitialRouteRef = useRef<string | null>(null);
  const [scrollSpyEpoch, setScrollSpyEpoch] = useState(0);
  const sectionPath = useCallback((id: string) => {
    return id === "about" ? "/" : "/#" + id;
  }, []);
  const updateSectionUrl = useCallback(
    (id: string) => {
      const path = sectionPath(id);
      // Next.js patches replaceState and re-renders the whole app for every
      // call that carries a URL, so a rewrite to the URL the page already has
      // (the About section on "/", on every load) is skipped instead of
      // spending a synchronous render on nothing.
      if (window.location.pathname + window.location.hash === path) return;
      try {
        window.history.replaceState(null, "", path);
      } catch {}
    },
    [sectionPath],
  );
  const clearUnlockHandlers = useCallback(() => {
    const timer = unlockTimerRef.current;
    if (timer) {
      window.clearTimeout(timer);
      unlockTimerRef.current = undefined;
    }
    const raf = unlockRafRef.current;
    if (raf) {
      cancelAnimationFrame(raf as unknown as number);
      unlockRafRef.current = undefined;
    }
  }, []);
  const waitForSectionAndUnlock = useCallback(
    (id: string) => {
      clearUnlockHandlers();
      const start = Date.now();
      const check = () => {
        const el = document.getElementById(id);
        if (el) {
          const rectTop = el.getBoundingClientRect().top;
          const top = Math.abs(rectTop - headerH - SCROLL_GAP);
          if (top <= 2 || Date.now() - start > 2000) {
            isProgrammaticRef.current = false;
            pendingTargetRef.current = null;
            unlockRafRef.current = undefined;
            setScrollSpyEpoch((currentEpoch) => currentEpoch + 1);
            return;
          }
        }
        unlockRafRef.current = requestAnimationFrame(
          check,
        ) as unknown as number;
      };
      unlockRafRef.current = requestAnimationFrame(check) as unknown as number;
    },
    [clearUnlockHandlers, headerH],
  );
  const goToSection = (id: string) => {
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) {
        const top =
          el.getBoundingClientRect().top +
          window.scrollY -
          headerH -
          SCROLL_GAP;
        if (prefersReducedMotion()) {
          window.scrollTo({ top, behavior: "auto" });
        } else {
          const html = document.documentElement;
          const body = document.body;
          const prevHtml = html.style.scrollBehavior;
          const prevBody = body.style.scrollBehavior;
          html.style.scrollBehavior = "smooth";
          body.style.scrollBehavior = "smooth";
          window.scrollTo({ top, behavior: "smooth" });
          setTimeout(() => {
            html.style.scrollBehavior = prevHtml;
            body.style.scrollBehavior = prevBody;
          }, 200);
        }
      }
    };
    if (isHomeRoute) {
      isProgrammaticRef.current = true;
      pendingTargetRef.current = id;
      setActiveId(id);
      scroll();
      updateSectionUrl(id);
      waitForSectionAndUnlock(id);
    } else {
      isProgrammaticRef.current = true;
      pendingTargetRef.current = id;
      setActiveId(id);
      onNav(sectionPath(id));
      setTimeout(scroll, 50);
    }
  };
  useEffect(() => {
    return () => {
      clearUnlockHandlers();
    };
  }, [clearUnlockHandlers]);
  // Scroll spy: one observer over the home sections marked data-section-spy,
  // owned here rather than by each section, so the sections need no client
  // code of their own. A section counts as current once it crosses the
  // middle of the viewport.
  useEffect(() => {
    if (!isHomeRoute) return;
    const sections =
      document.querySelectorAll<HTMLElement>("[data-section-spy]");
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Ignore scroll-driven updates while we're navigating programmatically
        if (isProgrammaticRef.current || pendingTargetRef.current) return;
        for (const entry of entries) {
          if (!entry.isIntersecting || !entry.target.id) continue;
          setActiveId(entry.target.id);
          updateSectionUrl(entry.target.id);
        }
      },
      { rootMargin: "-50% 0px -50% 0px" },
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [isHomeRoute, updateSectionUrl]);
  // Pin to About when near top to avoid early transitions after scrolling back
  useEffect(() => {
    const onTop = () => {
      if (!isHomeRoute) return;
      // Avoid overriding the active link during a programmatic scroll
      if (isProgrammaticRef.current || pendingTargetRef.current) return;
      if (window.scrollY <= headerH + SCROLL_GAP + 4 && activeId !== "about") {
        setActiveId("about");
        updateSectionUrl("about");
      }
    };
    window.addEventListener("scroll", onTop, { passive: true });
    return () => window.removeEventListener("scroll", onTop);
  }, [isHomeRoute, headerH, activeId, updateSectionUrl]);
  useEffect(() => {
    const onHashChange = () => {
      if (!isHomeRoute) return;
      const hash =
        typeof window !== "undefined" ? window.location.hash?.slice(1) : "";
      if (hash === "about") {
        updateSectionUrl("about");
        setActiveId("about");
        return;
      }
      if (hash && NAV_SECTIONS.some((session) => session.id === hash)) {
        setActiveId(hash);
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [isHomeRoute, updateSectionUrl]);
  useEffect(() => {
    if (!isHomeRoute) {
      startTransition(() => setActiveId(""));
      pendingTargetRef.current = null;
      isProgrammaticRef.current = false;
      return;
    }
    if (
      !pendingTargetRef.current &&
      handledInitialRouteRef.current !== route &&
      headerH > 0
    ) {
      const hash =
        typeof window !== "undefined" ? window.location.hash?.slice(1) : "";
      const id =
        hash && NAV_SECTIONS.some((session) => session.id === hash)
          ? hash
          : SECTION_ROUTES.includes(route)
            ? route.slice(1)
            : "";
      if (!id) return;
      if (id === "about") {
        updateSectionUrl("about");
      }
      handledInitialRouteRef.current = route;
      pendingTargetRef.current = id;
      startTransition(() => setActiveId(id));
      const el = document.getElementById(id);
      if (el) {
        const html = document.documentElement;
        const body = document.body;
        const prevHtml = html.style.scrollBehavior;
        const prevBody = body.style.scrollBehavior;
        html.style.scrollBehavior = "auto";
        body.style.scrollBehavior = "auto";
        const top =
          el.getBoundingClientRect().top +
          window.scrollY -
          headerH -
          SCROLL_GAP;
        window.scrollTo({ top, behavior: "auto" });
        html.style.scrollBehavior = prevHtml;
        body.style.scrollBehavior = prevBody;
      }
      waitForSectionAndUnlock(id);
      return;
    }
    if (pendingTargetRef.current) {
      const nextId = pendingTargetRef.current;
      if (nextId) {
        startTransition(() => setActiveId(nextId));
        if (headerH > 0) {
          const el = document.getElementById(nextId);
          if (el) {
            const html = document.documentElement;
            const body = document.body;
            const prevHtml = html.style.scrollBehavior;
            const prevBody = body.style.scrollBehavior;
            html.style.scrollBehavior = "auto";
            body.style.scrollBehavior = "auto";
            const top =
              el.getBoundingClientRect().top +
              window.scrollY -
              headerH -
              SCROLL_GAP;
            window.scrollTo({ top, behavior: "auto" });
            html.style.scrollBehavior = prevHtml;
            body.style.scrollBehavior = prevBody;
            waitForSectionAndUnlock(nextId);
          }
        }
      }
    }
    if (isProgrammaticRef.current || pendingTargetRef.current) {
      return;
    }
    // No-op observer; section changes are driven by InView components
    const observer = new IntersectionObserver(() => {});
    return () => observer.disconnect();
  }, [
    route,
    headerH,
    scrollSpyEpoch,
    isHomeRoute,
    waitForSectionAndUnlock,
    updateSectionUrl,
  ]);
  // On initial full page load with a hash, re-apply corrected scroll after the browser's default jump
  useEffect(() => {
    const correct = () => {
      if (!isHomeRoute || headerH <= 0) return;
      const hash =
        typeof window !== "undefined" ? window.location.hash?.slice(1) : "";
      if (!hash || !NAV_SECTIONS.some((session) => session.id === hash)) return;
      if (hash === "about") {
        updateSectionUrl("about");
      }
      isProgrammaticRef.current = true;
      pendingTargetRef.current = hash;
      setActiveId(hash);
      const el = document.getElementById(hash);
      if (el) {
        const html = document.documentElement;
        const body = document.body;
        const prevHtml = html.style.scrollBehavior;
        const prevBody = body.style.scrollBehavior;
        html.style.scrollBehavior = "auto";
        body.style.scrollBehavior = "auto";
        const top =
          el.getBoundingClientRect().top +
          window.scrollY -
          headerH -
          SCROLL_GAP;
        window.scrollTo({ top, behavior: "auto" });
        html.style.scrollBehavior = prevHtml;
        body.style.scrollBehavior = prevBody;
      }
      waitForSectionAndUnlock(hash);
    };
    // Run immediately in case load already fired before hydration
    requestAnimationFrame(() => correct());
    window.addEventListener("load", correct);
    return () => window.removeEventListener("load", correct);
  }, [isHomeRoute, headerH, waitForSectionAndUnlock, updateSectionUrl]);
  return (
    <>
      <header
        ref={headerRef}
        className="bg-background sticky top-0 z-50 border-b border-b-[rgba(0,0,0,0.08)] dark:border-b-[rgba(255,255,255,0.08)]"
      >
        <div
          data-page-gutter
          className="container mx-auto flex items-center justify-between py-3"
        >
          {route.startsWith("/session/") ? (
            <>
              <Button
                onClick={() => window.history.back()}
                aria-label="Go back"
                variant="outline"
                size="icon"
                data-testid="go-back-button"
                data-tooltip="Go back"
              >
                <ArrowLeft className="size-4" />
              </Button>
              <Link
                href="/"
                // Not prefetched: the header is on every page, so prefetching
                // pulls the whole home-route bundle into every page load.
                prefetch={false}
                className="text-foreground flex-1 text-center text-lg font-semibold tracking-tight no-underline"
              >
                François Martin
              </Link>
              <ThemeToggle />
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <Link
                  href="/"
                  // Not prefetched: the header is on every page, so prefetching
                  // pulls the whole home-route bundle into every page load.
                  prefetch={false}
                  className="text-foreground text-lg font-semibold tracking-tight no-underline"
                >
                  François Martin
                </Link>
              </div>
              <div className="hidden items-center gap-2 md:flex">
                <NavLinks
                  activeId={activeId}
                  isHomeRoute={isHomeRoute}
                  onNavigate={goToSection}
                />
                <ThemeToggle />
              </div>
              <div className="flex items-center gap-2 md:hidden">
                <ThemeToggle />
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Toggle menu"
                  aria-expanded={mobileOpen}
                  onClick={toggleMenu}
                  data-testid="toggle-menu-button"
                  data-tooltip={mobileOpen ? "Close menu" : "Open menu"}
                >
                  {mobileOpen ? (
                    <X className="size-4" />
                  ) : (
                    <Menu className="size-4" />
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </header>
      {!route.startsWith("/session/") &&
        (mobileOpen || menuAnimating || menuEntering) && (
          <div
            className={clsx(
              "bg-background fixed inset-x-0 z-40 origin-top transform-gpu border-y border-y-[rgba(0,0,0,0.08)] transition-[transform,opacity] duration-250 ease-out md:hidden dark:border-y-[rgba(255,255,255,0.08)]",
              mobileOpen && !menuEntering
                ? "scale-y-100 opacity-100"
                : "pointer-events-none scale-y-95 opacity-0",
            )}
            style={{
              top: headerH,
            }}
          >
            <div
              data-page-gutter
              className="container mx-auto flex flex-col gap-1 py-3"
            >
              <NavLinks
                className="justify-start"
                activeId={activeId}
                isHomeRoute={isHomeRoute}
                onNavigate={goToSection}
              />
            </div>
          </div>
        )}
    </>
  );
}
